import React, { useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import DialogWithEnterAction from '../../components/DialogWithEnterAction';
import { ActionButton } from '../../components/ActionButton';
import { useControllerConnectionStatus } from '../../Hooks/useControllerConnectionStatus';
import { GetHelp, InfoButton } from '../../FormWidgets/Description';
import { MyTimePicker } from '../../FormWidgets/DatePicker';
import {
  formatTimeParts,
  secondsToTimeParts,
  readTimeParts,
  timePartsToSeconds
} from '../../Utils/time_utils';
import { HandleAction, isZoned } from '../../Utils/utils';
import { supportsAdaptiveIrrigation } from '../../Utils/irrigation_utils';

const MANUAL_MAX_SECONDS = 60 * 60;

// Commands use the existing Action and controller Message paths. A successful
// browser send is only a submission; controller messages/status confirm results.
export default function IrrigationActions({ controller, parent, index, wsManager, resetOnly = false }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [dialog, setDialog] = useState(null);
  const [duration, setDuration] = useState(30);
  const [error, setError] = useState('');
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);
  const canWrite = Boolean(controller.access_rights?.irrigation?.write);
  const enabled = canWrite && connectionStatus === 'good';
  const dialogTitleId = `irrigation-${resetOnly ? 'reset' : 'manual'}-title-${index}`;
  const close = () => {
    setDialog(null);
    setError('');
  };

  const send = (action, value) => {
    if (controller.connection?.status !== 'good') return;
    if (
      !controller.access_rights?.irrigation?.write ||
      !supportsAdaptiveIrrigation(controller, parent.settings.irrigation.irrigation_settings[index])
    ) {
      setError(t('irrigation_action_unavailable'));
      return;
    }
    try {
      const sent = HandleAction(wsManager, controller, action, value, isZoned(controller) ? parent.zone_id : undefined);
      if (sent === false) throw new Error('send failed');
      enqueueSnackbar(t('irrigation_action_submitted'), { variant: 'info' });
      close();
    } catch (_) {
      setError(t('irrigation_action_send_failed'));
      enqueueSnackbar(t('irrigation_action_send_failed'), { variant: 'error' });
    }
  };
  const confirm = () => {
    if (dialog === 'reset') {
      send('action_irrigation_reset_adaptive', Number(index));
      return;
    }
    const seconds = Number(duration);
    if (!Number.isInteger(seconds) || seconds < 1 || seconds > MANUAL_MAX_SECONDS) {
      setError(t('irrigation_manual_duration_invalid'));
      return;
    }
    send(`action_irrigation_manual_shot_${index}`, seconds);
  };

  return (
    <>
      <Box display="flex" flexWrap="wrap" style={{ gap: '0.5rem' }}>
        {!resetOnly && (
          <>
            <ActionButton
              title={connectionDisabledReason}
              disabled={!enabled}
              onClick={() => {
                setDuration(30);
                setError('');
                setDialog('manual');
              }}
            >
              {t('irrigation_manual_shot')}
            </ActionButton>
            <ActionButton
              title={connectionDisabledReason}
              disabled={!enabled}
              onClick={() => send(`action_irrigation_manual_shot_${index}`, 0)}
            >
              {t('irrigation_manual_stop')}
            </ActionButton>
          </>
        )}
        {resetOnly && (
          <Box display="flex" alignItems="center">
            <ActionButton
              title={connectionDisabledReason}
              disabled={!enabled}
              onClick={() => {
                setError('');
                setDialog('reset');
              }}
            >
              {t('irrigation_reset_adaptive')}
            </ActionButton>
            <InfoButton {...GetHelp('irrigation_reset_adaptive')} />
          </Box>
        )}
      </Box>
      {error && !dialog && (
        <Typography color="error" role="alert">
          {error}
        </Typography>
      )}
      <DialogWithEnterAction
        open={dialog !== null}
        onClose={close}
        onEnterAction={confirm}
        enterActionDisabled={!enabled}
        aria-labelledby={dialogTitleId}
      >
        <DialogTitle id={dialogTitleId}>
          <span style={{ textTransform: 'none' }}>
            {t(dialog === 'reset' ? 'irrigation_reset_adaptive' : 'irrigation_manual_shot')}
            {` — ${t('irrigation')} ${Number(index) + 1}`}
          </span>
        </DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-line' }}>
            {t(
              dialog === 'reset' ? 'irrigation_reset_adaptive_text' : 'irrigation_manual_shot_text'
            )}
          </Typography>
          {dialog === 'manual' && (
            <>
              <Box display="flex" style={{ gap: '0.5rem', marginTop: '1rem' }}>
                {[30, 60, 120].map((seconds) => (
                  <Button
                    key={seconds}
                    variant={Number(duration) === seconds ? 'contained' : 'outlined'}
                    onClick={() => {
                      setDuration(seconds);
                      setError('');
                    }}
                  >
                    {seconds === 30 ? '30 sec' : `${seconds / 60} min`}
                  </Button>
                ))}
              </Box>
              <MyTimePicker
                id={`manual-irrigation-duration-${index}`}
                fullWidth
                seconds
                minSeconds={1}
                maxSeconds={MANUAL_MAX_SECONDS}
                disabled={!enabled}
                label={t('irrigation_manual_duration_seconds')}
                value={formatTimeParts(secondsToTimeParts(duration), true)}
                onChange={(date) => {
                  setDuration(timePartsToSeconds(readTimeParts(date)));
                  setError('');
                }}
              />
            </>
          )}
          {error && (
            <Typography color="error" role="alert">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('cancel')}</Button>
          <ActionButton title={connectionDisabledReason} disabled={!enabled} color="primary" onClick={confirm}>
            {t('apply')}
          </ActionButton>
        </DialogActions>
      </DialogWithEnterAction>
    </>
  );
}
